import { usePeopleManagementApi } from 'infrastructure/api';
import { OFFICE_NAMES, type OfficeType } from 'domain/shared/employee/officeService';
import {
  PerformanceMeasurementRank,
  QualificationLocationResponse,
  AvailableLocationToQualification,
  AvailableQualificationToLocation,
} from './types';
import useTechnologyService from 'domain/shared/career/technology/technologyService';
import { Option } from 'framework/form';
import { EMPLOYEE_COMPETENCY_LEVEL, QualificationCompetencyLevel } from './constants';

const usePerformanceMeasurementService = () => {
  const { sendPostRequest, sendGetRequest } = usePeopleManagementApi();
  const { findAll: findAllTechnologies } = useTechnologyService();
  const retrievePerformanceRank = async (request: any) => {
    const positionFilter = request?.filter.position;
    const locationFilter = request?.filter.officeId;
    const competencyLevelFilter = request?.filter.competencyLevel;

    const position = positionFilter ? positionFilter.in[0] : null;
    const location = locationFilter ? locationFilter.in[0] : null;
    const competencyLevel = competencyLevelFilter ? competencyLevelFilter.in[0] : null;

    const splitPosition = position ? position.split('--') : [];

    const positionId = splitPosition[0];
    const specializationId = splitPosition[1];

    if (positionId) {
      try {
        const customSearchCommand = {
          positionId,
          specialization: specializationId,
          officeId: location,
          competencyLevel,
        };

        const response = await sendPostRequest('/employees/performance-rank', customSearchCommand);
        const resultList = await response.json();

        return {
          totalElements: 1,
          pageNumber: 0,
          totalPages: 1,
          result: resultList.map((performanceRank: PerformanceMeasurementRank, index: number = 0) => ({
            index: index + 1,
            ...performanceRank,
          })),
        };
      } catch {
        return {
          totalElements: 1,
          pageNumber: 0,
          totalPages: 1,
          result: [],
        };
      }
    } else {
      return {
        totalElements: 1,
        pageNumber: 0,
        totalPages: 1,
        result: [],
      };
    }
  };

  const getPositionsWithTechnologies = async (): Promise<{
    allSpecializations: Array<{ label: string; value: string }>;
  }> => {
    const positionsWithTechnologiesResponse = await sendGetRequest('/positions/performance-rank-board');
    const positionsWithTechnologies = await positionsWithTechnologiesResponse.json();
    const allTechnologies = await findAllTechnologies();

    const allSpecializations = positionsWithTechnologies.flatMap((position: any) => {
      const technologies =
        position.technologies?.filter(Boolean)?.map((tech: any) => {
          const technologyName = allTechnologies.find(({ id }) => id === tech)?.name;
          return {
            label: `${technologyName ?? ''} ${position.name}`,
            value: position.id + '--' + tech,
          };
        }) ?? [];
      return technologies.length > 0
        ? technologies
        : [
            {
              label: position.name,
              value: position.id,
              id: position.id,
              name: position.name,
            },
          ];
    });

    return { allSpecializations };
  };

  const getPercentileCalculation = async () => {
    try {
      const percentileCalculationResponse = await sendGetRequest('/positions/percentile-calculation');
      const allTechnologies = await findAllTechnologies();
      if (!percentileCalculationResponse.ok) {
        return { allSpecializations: [] };
      }
      const percentileCalculation = await percentileCalculationResponse.json();

      const allSpecializations = percentileCalculation.flatMap((position: any) => {
        const technologies =
          position.technologies?.filter(Boolean)?.map((tech: any) => {
            const technologyName = allTechnologies.find(({ id }) => id === tech)?.name;
            return {
              label: `${technologyName ?? ''} ${position.name}`,
              value: position.id + '--' + tech,
              id: position.id,
              technologyId: tech,
              name: position.name,
            };
          }) ?? [];

        return technologies.length > 0
          ? technologies
          : [
              {
                label: position.name,
                value: position.id,
                id: position.id,
                name: position.name,
              },
            ];
      });
      return { allSpecializations };
    } catch (error) {
      return { allSpecializations: [] };
    }
  };

  const getPercentileCalculationByPosition = async (positionAndSpecialization: string) => {
    const { positionId, specializationId } = getPositionAndSpecialization(positionAndSpecialization);
    const technologyIdQuery = specializationId ? `&technologyId=${specializationId}` : '';

    const percentileCalculationResponse = await sendGetRequest(
      `/positions/qualifications-and-locations/percentile-calculation?positionId=${positionId}${technologyIdQuery}`,
    );
    return await percentileCalculationResponse.json();
  };

  const getQualificationAndLocations = async ({
    positionId,
    technologyId,
  }: {
    positionId: number;
    technologyId?: string;
  }): Promise<QualificationLocationResponse> => {
    const technologyIdQuery = technologyId ? `&technologyId=${technologyId}` : '';
    const qualificationsAndLocations = await sendGetRequest(
      `/positions/qualifications-and-locations/performance-rank-board?positionId=${positionId}${technologyIdQuery}`,
    );
    return qualificationsAndLocations.json();
  };

  const getCompetencyLevelOptions = (
    availableLocationsToQualification: AvailableLocationToQualification | undefined,
    availableQualificationToLocation: AvailableQualificationToLocation | undefined,
    selectedOfficeName?: string,
  ): Option[] => {
    if (!availableLocationsToQualification || !availableQualificationToLocation) {
      return [];
    }

    if (selectedOfficeName) {
      return Object.keys(EMPLOYEE_COMPETENCY_LEVEL).map(key => {
        return {
          value: key,
          label: key,
          disabled: !availableLocationsToQualification[selectedOfficeName.toUpperCase() || ''].some(
            location => location === key,
          ),
        };
      });
    }

    return Object.keys(EMPLOYEE_COMPETENCY_LEVEL).map(key => {
      return {
        value: key,
        label: key,
        disabled: !availableQualificationToLocation[key as keyof AvailableQualificationToLocation],
      };
    });
  };

  const getOfficeLocationOptions = (
    offices: OfficeType[],
    availableLocationsToQualification: AvailableLocationToQualification | undefined,
    availableQualificationToLocation: AvailableQualificationToLocation | undefined,
    competencyLevel?: QualificationCompetencyLevel,
  ): Option[] => {
    if (!offices) return [];
    if (!availableLocationsToQualification || !availableQualificationToLocation) {
      return offices.map((office: any) => {
        return {
          value: office.id,
          label: OFFICE_NAMES[office.name],
        };
      });
    }

    if (competencyLevel && competencyLevel in availableQualificationToLocation) {
      return offices.map((office: any) => {
        return {
          value: office.id,
          label: OFFICE_NAMES[office.name],
          disabled: !availableQualificationToLocation[competencyLevel].some(
            location => location === OFFICE_NAMES[office.name].toUpperCase(),
          ),
        };
      });
    }

    return offices.map((office: any) => {
      const officeName = OFFICE_NAMES[office.name];
      return {
        value: office.id,
        label: officeName,
        disabled: !availableLocationsToQualification[officeName.toUpperCase()],
      };
    });
  };

  const getPositionAndSpecialization = (
    position: string | null,
  ): { positionId?: number; specializationId?: string } => {
    if (!position) return { positionId: undefined, specializationId: undefined };
    const [posId, specId] = position.split('--');
    return { positionId: Number(posId), specializationId: specId };
  };

  return {
    retrievePerformanceRank,
    getPositionsWithTechnologies,
    getQualificationAndLocations,
    getCompetencyLevelOptions,
    getOfficeLocationOptions,
    getPercentileCalculation,
    getPercentileCalculationByPosition,
    getPositionAndSpecialization,
  };
};

export default usePerformanceMeasurementService;
